import { motion, AnimatePresence } from 'framer-motion';
import { OnboardingProgress } from './OnboardingProgress';
import { CreateFormStep } from './steps/CreateFormStep';
import { ConnectTelegramStep } from './steps/ConnectTelegramStep';
import { TestSubmissionStep } from './steps/TestSubmissionStep';
import { EmailSetupStep } from './steps/EmailSetupStep';
import { OnboardingComplete } from './OnboardingComplete';
import { useOnboarding } from '@/hooks/useOnboarding';

export function OnboardingWizard() {
  const {
    state,
    goToStep,
    completeStep,
    skipOnboarding,
    setFormId,
    markTelegramConnected,
    markTestSubmitted,
    markEmailConfigured,
    finishOnboarding,
  } = useOnboarding();

  const handleCreateFormComplete = (formId: string, formName: string) => {
    setFormId(formId, formName);
    completeStep(1);
    goToStep(2);
  };

  const handleTelegramComplete = () => {
    markTelegramConnected();
    completeStep(2);
    goToStep(3);
  };

  const handleTestComplete = () => {
    markTestSubmitted();
    completeStep(3);
    goToStep(4);
  };

  const handleEmailComplete = () => {
    markEmailConfigured();
    completeStep(4);
    finishOnboarding();
  };

  const handleSkipStep = () => {
    const currentStepNum = state.currentStep as number;
    if (currentStepNum === 4) {
      // On step 4, skipping completes onboarding
      finishOnboarding();
    } else if (currentStepNum === 1) {
      // Can't skip step 1 - need a form for other steps
      // Just skip entire onboarding
      skipOnboarding();
    } else {
      // Other steps, advance without completing
      goToStep((currentStepNum + 1) as 1 | 2 | 3 | 4);
    }
  };

  const handleBack = () => {
    const currentStepNum = state.currentStep as number;
    if (currentStepNum > 1) {
      goToStep((currentStepNum - 1) as 1 | 2 | 3 | 4);
    }
  };

  // Show completion screen
  if (state.currentStep === 'completed') {
    return <OnboardingComplete formId={state.formId} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="container max-w-2xl mx-auto px-4 py-8">
        {/* Header with skip */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Welcome to FormTo.cc</h1>
            <p className="text-gray-600">Let's set up your first form</p>
          </div>
          <button
            onClick={skipOnboarding}
            className="text-sm text-gray-500 hover:text-gray-700 hover:underline"
          >
            Skip setup
          </button>
        </div>

        {/* Progress indicator */}
        <OnboardingProgress
          currentStep={state.currentStep as number}
          completedSteps={state.completedSteps}
        />

        {/* Step content with animation */}
        <AnimatePresence mode="wait">
          <motion.div
            key={state.currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {state.currentStep === 1 && (
              <CreateFormStep onComplete={handleCreateFormComplete} onSkip={handleSkipStep} />
            )}
            {state.currentStep === 2 && state.formId && (
              <ConnectTelegramStep
                formId={state.formId}
                onComplete={handleTelegramComplete}
                onSkip={handleSkipStep}
                onBack={handleBack}
              />
            )}
            {state.currentStep === 3 && state.formId && (
              <TestSubmissionStep
                formId={state.formId}
                onComplete={handleTestComplete}
                onSkip={handleSkipStep}
                onBack={handleBack}
              />
            )}
            {state.currentStep === 4 && state.formId && (
              <EmailSetupStep
                formId={state.formId}
                onComplete={handleEmailComplete}
                onSkip={handleSkipStep}
                onBack={handleBack}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
